/**
 * dashboardDataScope — #122 dashboard DATA-SCOPE toggle for parent-org users.
 *
 * A user whose company HAS sub-companies (≥1 descendant in the verified
 * hierarchy) can narrow what the dashboard shows along the company axis:
 *
 *   'own'            — only the user's OWN company (plus their personal,
 *                      not-yet-company-shared sites/reports, which carry no
 *                      companyId — they belong to the user, i.e. their org).
 *   'all'            — the user's FULL subtree (own + every descendant). This
 *                      is the pre-#122 behaviour and the default.
 *   'specific:<id>'  — a single descendant org. Restricted to that one
 *                      company's id ONLY.
 *
 * SECURITY: this is a PURE CLIENT-SIDE narrowing over already-authorized
 * data. `fetchSites` / `fetchRagReports` already load the whole subtree
 * (gated by Firestore rules + `resolveSubtreeCompanyIds`); this scope only
 * ever SUBSETS that set. `resolveScopeCompanyIds` intersects the requested
 * scope with the authorized subtree, so a tampered `specific:` id that is
 * NOT in the subtree collapses to the empty/own set — it can never widen
 * access beyond what the user already fetched.
 *
 * The resolution is a PURE function (`resolveScopeCompanyIds`) over the
 * authorized subtree id set, mirroring `filterSitesBySource`'s pure-predicate
 * design so the consumer derives the filtered subset in a `useMemo` over the
 * `siteRag.sites` ROOT — never inside a Zustand selector.
 */

/** Persisted scope value. `'specific:<companyId>'` encodes a single sub-org. */
export type DashboardDataScope = 'own' | 'all' | `specific:${string}`;

const SPECIFIC_PREFIX = 'specific:';

/** Narrow an arbitrary string into a `specific:<id>` scope, or null. */
export function parseSpecificScope(scope: DashboardDataScope): string | null {
  if (scope.startsWith(SPECIFIC_PREFIX)) {
    const id = scope.slice(SPECIFIC_PREFIX.length);
    return id.trim().length > 0 ? id : null;
  }
  return null;
}

/** Build the encoded scope value for a specific sub-company. */
export function specificScope(companyId: string): DashboardDataScope {
  return `${SPECIFIC_PREFIX}${companyId}`;
}

/**
 * Resolve the SCOPE company-id set: which company ids the dashboard should
 * show for the given scope, GIVEN the user's own company and their fully
 * authorized subtree id set (own + descendants, from
 * `resolveSubtreeCompanyIds` / `collectDescendantCompanyIds`).
 *
 * Returns a `Set<string>` of allowed company ids. The result is ALWAYS a
 * subset of `subtreeCompanyIds` ∪ {ownCompanyId} — it never widens access.
 *
 *   'own'           → { ownCompanyId }
 *   'all'           → the full subtree set (own + descendants)
 *   'specific:<id>' → { id } IFF id ∈ subtree, else falls back to { ownCompanyId }
 *
 * @param scope            The requested data scope.
 * @param ownCompanyId     The user's own company id (auth claim). May be null
 *                         for solo users — they have no subtree and should not
 *                         see the selector, but we degrade safely to an empty
 *                         set so a stray call can't widen anything.
 * @param subtreeCompanyIds The authorized subtree ids (own + descendants).
 */
export function resolveScopeCompanyIds(
  scope: DashboardDataScope,
  ownCompanyId: string | null | undefined,
  subtreeCompanyIds: readonly string[],
): Set<string> {
  const own = typeof ownCompanyId === 'string' && ownCompanyId.trim().length > 0
    ? ownCompanyId
    : null;
  // The authoritative authorized universe: the fetched subtree, defensively
  // unioned with the own id (so a sparse/failed subtree resolution still lets
  // the user see their own org).
  const authorized = new Set<string>(subtreeCompanyIds.filter((id) => id.trim().length > 0));
  if (own) authorized.add(own);

  if (scope === 'own') {
    return own ? new Set([own]) : new Set();
  }

  if (scope === 'all') {
    return authorized;
  }

  const specificId = parseSpecificScope(scope);
  if (specificId && authorized.has(specificId)) {
    return new Set([specificId]);
  }
  // Tampered / stale specific id outside the subtree → never widen; fall back
  // to the user's own org (or empty when they have none).
  return own ? new Set([own]) : new Set();
}

/**
 * Predicate: does an entity owned by `entityCompanyId` fall within the resolved
 * scope set? Used to filter both sites and reports.
 *
 * Entities with NO company id (personal / not-yet-shared sites and reports,
 * whose `companyId` is empty) belong to the SIGNED-IN USER, i.e. their own
 * org. They are included when the scope set contains the user's `ownCompanyId`
 * (the 'own' and 'all' scopes), and excluded for a 'specific' sub-company
 * scope — a sub-org view should not surface the parent user's personal sites.
 *
 * @param entityCompanyId  The owning company id of a site/report (may be empty).
 * @param scopeIds         The resolved scope set from `resolveScopeCompanyIds`.
 * @param ownCompanyId     The user's own company id, to attribute companyless
 *                         entities. When the scope set contains it, companyless
 *                         entities are in scope.
 */
export function isEntityInScope(
  entityCompanyId: string | null | undefined,
  scopeIds: Set<string>,
  ownCompanyId: string | null | undefined,
): boolean {
  const hasCompany = typeof entityCompanyId === 'string' && entityCompanyId.trim().length > 0;
  if (hasCompany) {
    return scopeIds.has(entityCompanyId as string);
  }
  // Companyless entity → owned by the signed-in user. In scope iff the scope
  // set covers the user's own org.
  const own = typeof ownCompanyId === 'string' && ownCompanyId.trim().length > 0
    ? ownCompanyId
    : null;
  return own !== null && scopeIds.has(own);
}

/** localStorage key for the persisted scope, namespaced per user. */
export function dataScopeStorageKey(uid: string | null | undefined): string {
  return `dashboard:dataScope:${uid ?? 'anon'}`;
}

/**
 * Read the persisted scope for a user. Defaults to `'all'` (the pre-#122
 * behaviour) when nothing is stored or storage is unavailable. A stored
 * `specific:<id>` whose id is no longer in the subtree is the consumer's job
 * to reconcile (it degrades to own via `resolveScopeCompanyIds`); we return it
 * verbatim here so the selector can detect + clear a stale pin.
 */
export function readPersistedScope(uid: string | null | undefined): DashboardDataScope {
  try {
    const raw = globalThis.localStorage?.getItem(dataScopeStorageKey(uid));
    if (raw === 'own' || raw === 'all') return raw;
    if (typeof raw === 'string' && raw.startsWith(SPECIFIC_PREFIX) && parseSpecificScope(raw as DashboardDataScope)) {
      return raw as DashboardDataScope;
    }
  } catch {
    // Storage blocked (private mode, SecurityError) — fall through to default.
  }
  return 'all';
}

/** Persist the scope for a user. Best-effort; swallows storage failures. */
export function writePersistedScope(uid: string | null | undefined, scope: DashboardDataScope): void {
  try {
    globalThis.localStorage?.setItem(dataScopeStorageKey(uid), scope);
  } catch {
    // Quota / private mode — non-fatal; the in-memory state still applies.
  }
}
